// src/components/admin/AdminContentPage.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    CircularProgress,
    Stack,
    TextField,
    Button,
    Snackbar,
    Alert,
    useMediaQuery,
    Grid,
    Breadcrumbs,
    Link,
    Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import TableComponent from '../../widgets/TableComponent';
import RequestService from '../../services/RequestService';
import ContentList from '../../widgets/contents/ContentList';
import ContentDeleteDialog from '../../widgets/contents/ContentDeleteDialog';
import AdminContentDialog from '../../widgets/admin/AdminContentDialog';
import AdminNewContentDialog from '../../widgets/admin/AdminNewContentDialog';
import { ReactComponent as CoinIcon } from '../../assets/icons/coin.svg';
import { ReactComponent as XpIcon } from '../../assets/icons/xp.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import RewardBadge from '../../widgets/RewardBadge';
import AdminDeleteContent from '../../widgets/admin/AdminDeleteContent';

const AdminContentPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [contents, setContents] = useState([]);
    const [groups, setGroups] = useState([]);
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [groupId, setGroupId] = useState('');
    const [gameId, setGameId] = useState('');
    const [type, setType] = useState('');

    const [selectedContent, setSelectedContent] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, msg: '' });
    const [dialogs, setDialogs] = useState({
        new: false,
        edit: false,
        delete: false,
    });

    useEffect(() => {
        RequestService.getCompanies().then(setCompanies);
    }, []);

    const fetchContents = async () => {
        if (!selectedCompanyId) return;
        setLoading(true);
        try {
            const data = await RequestService.getContentsAsSuperAdmin(
                selectedCompanyId,
                searchTerm,
                groupId,
                gameId,
                type
            );
            setContents(data || []);
        } catch (err) {
            setContents([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedCompanyId) {
            fetchContents();
        }
    }, [selectedCompanyId]);

    const handleCompanyChange = async (e) => {
        const companyId = e.target.value;
        setSelectedCompanyId(companyId);
        setContents([]);
        setGroupId('');
        setGameId('');
        setType('');
        setSearchTerm('');

        try {
            const [groupData, gameData] = await Promise.all([
                RequestService.getGroupsAsSuperAdmin(companyId),
                RequestService.getGamesAsSuperAdmin(companyId),
            ]);

            setGroups(groupData || []);
            setGames(gameData || []);
        } catch (err) {
            console.error('Erro ao buscar filtros:', err);
            setGroups([]);
            setGames([]);
        }
    };

    const handleRowClick = (content) => {
        setSelectedContent(content);
        setDialogs({ ...dialogs, edit: true });
    };

    const columns = [
        {
            label: 'Título e Descrição',
            field: 'title',
            align: 'left',
            renderCell: (value, row) => (
                <Stack direction="column" spacing={1}>
                    <Typography variant="body2" sx={{ color: '#292929' }}>
                        {row.title}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ fontSize: '14px', color: '#292929' }}
                    >
                        {row.description}
                    </Typography>
                </Stack>
            ),
        },
        {
            label: 'Jogos',
            field: 'games',
            align: 'center',
            renderCell: (value, row) => (
                <ChipGroup items={row.games} labelKey="name" />
            ),
        },
        {
            label: 'Turma',
            field: 'groups',
            align: 'center',
            renderCell: (value, row) => (
                <ChipGroup items={row.groups} labelKey="name" />
            ),
        },
        {
            label: 'Recompensa',
            field: 'reward',
            align: 'center',
            renderCell: (value, row) => (
                <Stack direction="column" spacing={2} alignItems="center">
                    <RewardBadge
                        icon={
                            <CoinIcon
                                style={{ color: '#134F99', fontSize: '24px' }}
                            />
                        }
                        value={row.coin}
                        positive={row.attitudeType !== 'NEGATIVE'}
                    />
                    <RewardBadge
                        icon={
                            <XpIcon
                                style={{ color: '#134F99', fontSize: '24px' }}
                            />
                        }
                        value={row.exp}
                        positive={row.attitudeType !== 'NEGATIVE'}
                    />
                </Stack>
            ),
        },
        {
            label: '',
            field: 'actions',
            align: 'right',
            renderCell: (value, row) => (
                <Stack direction="row" spacing={2}>
                    <Link
                        component="button"
                        underline="none"
                        onClick={() => handleRowClick(row)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'blue',
                        }}
                    >
                        <EditIcon sx={{ color: '#1F83FF' }} />
                        <Typography
                            variant="body2"
                            sx={{ ml: 0.5, color: '#1F83FF' }}
                        >
                            Editar
                        </Typography>
                    </Link>
                    <Link
                        component="button"
                        underline="none"
                        onClick={() => {
                            setSelectedContent(row);
                            setDialogs((d) => ({ ...d, delete: true }));
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'red',
                        }}
                    >
                        <DeleteOutlinedIcon fontSize="small" />
                        <Typography variant="body2" sx={{ ml: 0.5 }}>
                            Excluir
                        </Typography>
                    </Link>
                </Stack>
            ),
        },
    ];

    const ChipGroup = ({ items, labelKey }) => {
        if (!items || items.length === 0) return null;

        const visibleItems = items.slice(0, 2);
        const remainingCount = items.length - 2;

        return (
            <Stack direction="row" spacing={1}>
                {visibleItems.map((item) => (
                    <Chip
                        key={item.id}
                        label={item[labelKey]}
                        size="small"
                        color="primary"
                    />
                ))}
                {remainingCount > 0 && (
                    <Chip
                        label={`+${remainingCount}`}
                        size="small"
                        color="default"
                    />
                )}
            </Stack>
        );
    };

    const renderFilters = () => (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    label="Pesquisar Nome"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchContents()}
                    fullWidth
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                    <InputLabel>Grupo</InputLabel>
                    <Select
                        value={groupId}
                        onChange={(e) => setGroupId(e.target.value)}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {groups.map((g) => (
                            <MenuItem key={g.id} value={g.id}>
                                {g.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                    <InputLabel>Jogo</InputLabel>
                    <Select
                        value={gameId}
                        onChange={(e) => setGameId(e.target.value)}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {games.map((g) => (
                            <MenuItem key={g.id} value={g.id}>
                                {g.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="MISSION">Missão</MenuItem>
                        <MenuItem value="ATTITUDE">Atitude</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
    );

    const renderCompanySelect = () => (
        <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel>Empresa</InputLabel>
            <Select
                value={selectedCompanyId}
                onChange={handleCompanyChange}
                label="Empresa"
            >
                {companies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                        {c.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const renderEmpty = () => (
        <Typography sx={{ mt: 4 }} color="text.secondary" align="center">
            {selectedCompanyId
                ? 'Nenhum conteúdo encontrado.'
                : 'Selecione uma empresa para visualizar os conteúdos.'}
        </Typography>
    );

    const renderMobileLayout = () =>
        contents.length ? (
            <ContentList
                contents={contents}
                onUserClick={(c) => {
                    setSelectedContent(c);
                    setDialogs((d) => ({ ...d, edit: true }));
                }}
                onDeleteClick={(c) => {
                    setSelectedContent(c);
                    setDialogs((d) => ({ ...d, delete: true }));
                }}
            />
        ) : (
            renderEmpty()
        );

    const renderDesktopLayout = () => {
        return contents.length === 0 ? (
            renderEmpty()
        ) : (
            <Box sx={{ width: '100%', overflowX: 'auto', mt: 2 }}>
                <TableComponent columns={columns} rows={contents} />
            </Box>
        );
    };

    return (
        <Box p={4}>
            <Breadcrumbs>
                <Link underline="hover" color="inherit" href="/admin">
                    Início
                </Link>
                <Typography color="text.primary">Missões</Typography>
            </Breadcrumbs>

            <Typography variant="h4" sx={{ mt: 2, mb: 1 }}>
                Gerenciar Missões
            </Typography>

            {renderCompanySelect()}
            {selectedCompanyId && renderFilters()}

            <Box mt={2}>
                {loading ? (
                    <Stack alignItems="center" mt={4}>
                        <CircularProgress />
                    </Stack>
                ) : isMobile ? (
                    renderMobileLayout()
                ) : (
                    renderDesktopLayout()
                )}
            </Box>

            <Box mt={4}>
                <Button
                    variant="contained"
                    endIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={() => setDialogs((d) => ({ ...d, new: true }))}
                >
                    Nova Missão
                </Button>
            </Box>

            <AdminNewContentDialog
                open={dialogs.new}
                onClose={() => setDialogs((d) => ({ ...d, new: false }))}
                onSuccess={(msg) => {
                    setSnackbar({ open: true, msg });
                    fetchContents();
                }}
                company={selectedCompanyId}
            />

            <AdminContentDialog
                open={dialogs.edit}
                onClose={() => setDialogs((d) => ({ ...d, edit: false }))}
                content={selectedContent}
                onSuccess={(msg) => {
                    setSnackbar({ open: true, msg });
                    fetchContents();
                }}
                company={selectedCompanyId}
            />

            <AdminDeleteContent
                open={dialogs.delete}
                onClose={() => setDialogs((d) => ({ ...d, delete: false }))}
                content={selectedContent}
                onSuccess={() => {
                    setSnackbar({
                        open: true,
                        msg: 'Conteúdo excluído com sucesso!',
                    });
                    fetchContents();
                }}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity="success"
                    variant="filled"
                >
                    {snackbar.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminContentPage;
